import { Check, Copy } from "lucide-react";
import React, { FC } from "react";

import type { ButtonProps } from "@mui/material";

import { useCopyToClipboard } from "./hooks/use-copy-to-clipboard";
import { IgButton } from "./IgButton";

export interface IgCopyButtonProps extends Omit<
  ButtonProps,
  "children" | "onClick" | "startIcon"
> {
  /** String written to the clipboard when the button is clicked. */
  text: string;
  /** Label shown in the idle state. Defaults to "Copy link". */
  label?: string;
  /** Label shown briefly after a successful copy. Defaults to "Copied". */
  copiedLabel?: string;
  /** Pixel size of the leading copy/confirmation icon. */
  iconSize?: number;
  /** Switch the button to the success color while showing the copied state. */
  successColorOnCopy?: boolean;
}

/**
 * Copies a string to the clipboard, swapping the icon/label for a confirmation
 * that resets after a short delay. Use for "copy link"/"copy value" actions
 * that read as buttons; for inline click-to-copy text use IgCopyTextTypography,
 * and for an icon-only affordance use IgCopyIconButton.
 */
export const IgCopyButton: FC<IgCopyButtonProps> = ({
  text,
  label = "Copy link",
  copiedLabel = "Copied",
  iconSize = 14,
  successColorOnCopy = false,
  color,
  size = "small",
  variant = "outlined",
  ...rest
}) => {
  const { copied, copy } = useCopyToClipboard();

  return (
    <IgButton
      {...rest}
      color={successColorOnCopy && copied ? "success" : color}
      onClick={() => void copy(text)}
      size={size}
      startIcon={copied ? <Check size={iconSize} /> : <Copy size={iconSize} />}
      variant={variant}
    >
      {copied ? copiedLabel : label}
    </IgButton>
  );
};
